import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Share2,
  MoreVertical,
  Calendar,
  Clock,
  MapPin,
  Users,
  MessageCircle,
  Check,
  X,
  AlertCircle,
  Flame,
  Sparkles,
} from 'lucide-react';
import {
  Heading,
  Text,
  Stack,
  Container,
  Divider,
} from '@/components/ui/index.js';
import { useEventsStore } from '@/features/events/store/useEventsStore.js';
import { useUIStore } from '@/stores/uiStore.js';
import { HostCard } from './components/HostCard/index.js';
import { ParticipantList } from './components/ParticipantList/index.js';
import { LocationMap } from './components/LocationMap/index.js';
import { AmenityGrid } from './components/AmenityGrid/index.js';
import styles from './EventDetailPage.module.css';

/**
 * EventDetailPage - Detailed event view with full information
 * Features: Hero section, event info, participants, location, amenities, related events
 */
export function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useUIStore();

  const {
    currentEvent,
    isLoading,
    fetchEvent,
    joinEvent,
    leaveEvent,
    getRelatedEvents,
    clearCurrentEvent,
  } = useEventsStore();

  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  // Fetch event data on mount
  useEffect(() => {
    fetchEvent(id);
    return () => clearCurrentEvent();
  }, [id, fetchEvent, clearCurrentEvent]);

  // Handle back navigation
  const handleBack = () => {
    navigate('/events');
  };

  // Handle share
  const handleShare = async () => {
    const shareData = {
      title: currentEvent?.title || 'Check out this event on SportsSpots',
      text: currentEvent?.description?.slice(0, 100) + '...',
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        showSuccess('Link copied to clipboard!');
      }
    } catch (err) {
      // User cancelled share
    }
  };

  // Handle more options
  const handleMoreOptions = () => {
    // Could open a modal with more options (report, block, etc.)
    showInfo('More options coming soon!');
  };

  // Handle join/leave event
  const handleJoinToggle = async () => {
    if (isJoining || !currentEvent) return;

    setIsJoining(true);

    let result;
    if (currentEvent.isJoined) {
      result = await leaveEvent(id);
    } else {
      result = await joinEvent(id);
    }

    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }

    setIsJoining(false);
  };

  // Handle message host
  const handleMessageHost = () => {
    showInfo('Messaging coming soon!');
  };

  // Handle view profile
  const handleViewProfile = () => {
    if (currentEvent?.host?.id) {
      navigate(`/profile/${currentEvent.host.id}`);
    }
  };

  // Handle view all participants
  const handleViewAllParticipants = () => {
    showInfo('Full participant list coming soon!');
  };

  // Get sport icon/emoji
  const getSportIcon = (sport) => {
    const icons = {
      basketball: '🏀',
      soccer: '⚽',
      tennis: '🎾',
      volleyball: '🏐',
      yoga: '🧘',
      running: '🏃',
      cycling: '🚴',
      swimming: '🏊',
    };
    return icons[sport] || '🏆';
  };

  // Get skill level color
  const getSkillLevelColor = (level) => {
    const colors = {
      beginner: styles.skillBeginner,
      intermediate: styles.skillIntermediate,
      advanced: styles.skillAdvanced,
      'all-levels': styles.skillAllLevels,
    };
    return colors[level] || '';
  };

  // Get button state
  const getButtonState = () => {
    if (!currentEvent) return { text: 'Loading...', variant: 'loading' };

    if (currentEvent.status === 'cancelled') {
      return { text: 'Cancelled', variant: 'cancelled', disabled: true };
    }

    if (currentEvent.participants.count >= currentEvent.participants.max) {
      return { text: 'Full', variant: 'full', disabled: true };
    }

    if (currentEvent.isJoined) {
      return { text: 'Leave Event', variant: 'leave' };
    }

    return { text: 'Join Event', variant: 'join' };
  };

  // Truncate description
  const truncateDescription = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + '...';
  };

  // Loading state
  if (isLoading || !currentEvent) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <Text size="base" color="secondary">
          Loading event details...
        </Text>
      </div>
    );
  }

  const buttonState = getButtonState();
  const relatedEvents = getRelatedEvents(id, 4);
  const descriptionToShow = showFullDescription
    ? currentEvent.description
    : truncateDescription(currentEvent.description);
  const hasMoreDescription = currentEvent.description.length > 200;

  return (
    <div className={styles.page}>
      {/* Header Navigation */}
      <header className={styles.header}>
        <Container size="full" padding className={styles.headerContainer}>
          <Stack direction="row" justify="space-between" align="center" className={styles.headerContent}>
            <button
              className={styles.iconButton}
              onClick={handleBack}
              aria-label="Go back"
            >
              <ArrowLeft size={24} />
            </button>

            <Stack direction="row" gap={2}>
              <button
                className={styles.iconButton}
                onClick={handleShare}
                aria-label="Share event"
              >
                <Share2 size={22} />
              </button>
              <button
                className={styles.iconButton}
                onClick={handleMoreOptions}
                aria-label="More options"
              >
                <MoreVertical size={22} />
              </button>
            </Stack>
          </Stack>
        </Container>
      </header>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroBackground}>
          <div className={styles.heroGradient} />
        </div>

        <Container size="full" padding className={styles.heroContent}>
          <div className={styles.sportIcon}>{getSportIcon(currentEvent.sport)}</div>

          {currentEvent.isLive && (
            <div className={styles.liveIndicator}>
              <Flame size={14} className={styles.liveIcon} />
              <Text size="xs" weight="semibold" color="primary">
                LIVE NOW
              </Text>
            </div>
          )}

          <div className={styles.sportBadge}>
            <Text size="xs" weight="medium" color="primary">
              {currentEvent.sportLabel}
            </Text>
          </div>
        </Container>
      </section>

      {/* Main Content */}
      <main className={styles.main}>
        <Container size="full" padding className={styles.content}>
          {/* Event Info Section */}
          <section className={styles.section} style={{ animationDelay: '0ms' }}>
            <div className={styles.titleRow}>
              <Heading level={1} size="xl" className={styles.title}>
                {currentEvent.title}
              </Heading>
              <div className={`${styles.skillBadge} ${getSkillLevelColor(currentEvent.skillLevel)}`}>
                <Text size="xs" weight="medium">
                  {currentEvent.skillLabel}
                </Text>
              </div>
            </div>

            <HostCard host={currentEvent.host} onViewProfile={handleViewProfile} />

            <Divider color="default" spacing="sm" />

            {/* Event Meta */}
            <Stack direction="column" gap={3} className={styles.metaList}>
              <div className={styles.metaItem}>
                <Calendar size={20} className={styles.metaIcon} />
                <div className={styles.metaContent}>
                  <Text size="base" weight="medium" color="primary">
                    {currentEvent.date}
                  </Text>
                </div>
              </div>

              <div className={styles.metaItem}>
                <Clock size={20} className={styles.metaIcon} />
                <div className={styles.metaContent}>
                  <Text size="base" weight="medium" color="primary">
                    {currentEvent.time}
                  </Text>
                </div>
              </div>

              <div className={styles.metaItem}>
                <MapPin size={20} className={styles.metaIcon} />
                <div className={styles.metaContent}>
                  <Text size="base" weight="medium" color="primary">
                    {currentEvent.location.name}
                  </Text>
                </div>
              </div>

              <div className={styles.metaItem}>
                <Users size={20} className={styles.metaIcon} />
                <div className={styles.metaContent}>
                  <Text size="base" weight="medium" color="primary">
                    {currentEvent.participants.count} / {currentEvent.participants.max} joined
                  </Text>
                </div>
              </div>
            </Stack>

            {/* Price Badge */}
            <div className={styles.priceBadge}>
              {currentEvent.price === 0 ? (
                <>
                  <Sparkles size={16} className={styles.priceIcon} />
                  <Text size="sm" weight="medium" color="lime">
                    Free
                  </Text>
                </>
              ) : (
                <>
                  <Text size="sm" weight="medium" color="primary">
                    ${currentEvent.price}
                  </Text>
                </>
              )}
            </div>
          </section>

          {/* Description Section */}
          <section className={styles.section} style={{ animationDelay: '100ms' }}>
            <Heading level={2} size="md" className={styles.sectionTitle}>
              About This Event
            </Heading>

            <div className={styles.description}>
              <Text size="base" color="secondary" leading="relaxed">
                {descriptionToShow}
              </Text>

              {hasMoreDescription && (
                <button
                  className={styles.showMoreButton}
                  onClick={() => setShowFullDescription(!showFullDescription)}
                >
                  <Text size="sm" color="cyan">
                    {showFullDescription ? 'Show Less' : 'Show More'}
                  </Text>
                </button>
              )}
            </div>
          </section>

          {/* Participants Section */}
          <section className={styles.section} style={{ animationDelay: '200ms' }}>
            <ParticipantList
              participants={currentEvent.participants}
              onViewAll={handleViewAllParticipants}
              friendsAttending={2}
            />
          </section>

          {/* Location Section */}
          <section className={styles.section} style={{ animationDelay: '300ms' }}>
            <LocationMap location={currentEvent.location} />
          </section>

          {/* Amenities Section */}
          <section className={styles.section} style={{ animationDelay: '400ms' }}>
            <AmenityGrid amenities={currentEvent.amenities} />
          </section>

          {/* Related Events Section */}
          <section className={`${styles.section} ${styles.relatedSection}`} style={{ animationDelay: '500ms' }}>
            <Heading level={2} size="md" className={styles.sectionTitle}>
              Similar Events
            </Heading>

            <div className={styles.relatedEvents}>
              {relatedEvents.map((event) => (
                <div
                  key={event.id}
                  className={styles.relatedCard}
                  onClick={() => navigate(`/events/${event.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/events/${event.id}`)}
                >
                  <div className={styles.relatedIcon}>{getSportIcon(event.sport)}</div>
                  <div className={styles.relatedContent}>
                    <Text size="sm" weight="semibold" color="primary" className={styles.relatedTitle}>
                      {event.title}
                    </Text>
                    <Text size="xs" color="secondary">
                      {event.date} · {event.time}
                    </Text>
                    <Text size="xs" color="muted">
                      {event.location}
                    </Text>
                    <div className={styles.relatedMeta}>
                      <Users size={12} className={styles.relatedMetaIcon} />
                      <Text size="xs" color="muted">
                        {event.participants}/{event.maxParticipants}
                      </Text>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Bottom Spacer for Action Bar */}
          <div className={styles.bottomSpacer} />
        </Container>
      </main>

      {/* Action Bar */}
      <div className={styles.actionBar}>
        <Container size="full" padding className={styles.actionBarContent}>
          <Stack direction="row" gap={3} align="center" className={styles.actionBarInner}>
            <button
              className={`${styles.actionButton} ${styles.messageButton}`}
              onClick={handleMessageHost}
              aria-label="Message host"
            >
              <MessageCircle size={20} />
            </button>

            <button
              className={`${styles.joinButton} ${styles[buttonState.variant]}`}
              onClick={handleJoinToggle}
              disabled={buttonState.disabled || isJoining}
              aria-label={buttonState.text}
            >
              {isJoining ? (
                <div className={styles.buttonSpinner} />
              ) : buttonState.variant === 'leave' ? (
                <X size={20} className={styles.buttonIcon} />
              ) : buttonState.variant === 'join' ? (
                <Check size={20} className={styles.buttonIcon} />
              ) : (
                <AlertCircle size={20} className={styles.buttonIcon} />
              )}
              <span>{isJoining ? 'Processing...' : buttonState.text}</span>
            </button>
          </Stack>
        </Container>
      </div>
    </div>
  );
}

export default EventDetailPage;
